/*
 * Copyright (c) 2025, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  Container,
  Grid,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  IconButton,
  SelectChangeEvent,
  Tooltip,
  Radio,
  FormLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import theme from './theme';

interface IntegrationType {
  value: string;
  label: string;
  description: string;
}

const integrationTypes: IntegrationType[] = [
  { value: 'default', label: 'Automation', description: 'General automation component' },
  { value: 'ai', label: 'AI Agent', description: 'AI-powered agent component' },
  { value: 'api', label: 'Integration as API', description: 'API integration component' },
  { value: 'event', label: 'Event Integration', description: 'Event-driven integration' },
  { value: 'file', label: 'File Integration', description: 'File-based integration' },
];

function App() {
  const [githubUrl, setGithubUrl] = useState('');
  const [integrationType, setIntegrationType] = useState('');
  const [branch, setBranch] = useState('');
  const [path, setPath] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState<{ [key: string]: boolean }>({});
  const [buttonStyle, setButtonStyle] = useState<'light' | 'dark'>('light');

  const parseRepositoryUrl = (input: string) => {
    const trimmed = input.trim().replace(/\/$/, '');

    // Handle short format (username/repo) - assume GitHub
    if (!trimmed.startsWith('http')) {
      const parts = trimmed.split('/');
      if (parts.length >= 2) {
        return {
          owner: parts[0],
          repo: parts[1],
          platform: 'github',
          valid: true,
        };
      }
      return { valid: false };
    }

    // Handle full URLs (GitHub, GitLab, Bitbucket)
    try {
      const url = new URL(trimmed);
      let platform = '';
      
      if (url.hostname === 'github.com') {
        platform = 'github';
      } else if (url.hostname === 'gitlab.com') {
        platform = 'gitlab';
      } else if (url.hostname === 'bitbucket.org') {
        platform = 'bitbucket';
      } else {
        return { valid: false };
      }

      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length >= 2) {
        return {
          owner: pathParts[0],
          repo: pathParts[1],
          platform,
          fullUrl: trimmed,
          valid: true,
        };
      }
    } catch {
      return { valid: false };
    }

    return { valid: false };
  };

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCopySuccess({});

    if (!githubUrl.trim()) {
      setError('Please enter a repository URL');
      return;
    }

    if (!integrationType) {
      setError('Please select an integration type');
      return;
    }

    const parsed = parseRepositoryUrl(githubUrl);
    if (!parsed.valid) {
      setError('Please enter a valid repository URL (GitHub, GitLab, or Bitbucket) or username/repository format');
      return;
    }

    // Build repository parameter based on platform
    let repoParam = '';
    const paramName = parsed.platform === 'github' ? 'gh' : 'repo';
    
    // Use 'main' as default branch if path is provided but no branch is specified
    const effectiveBranch = branch || (path ? 'main' : '');
    
    if (parsed.platform === 'github') {
      // GitHub: use short format
      repoParam = `${parsed.owner}/${parsed.repo}`;
      if (effectiveBranch) {
        repoParam += `/tree/${effectiveBranch}`;
        if (path) {
          repoParam += `/${path}`;
        }
      }
    } else {
      // GitLab/Bitbucket: use full URL
      repoParam = parsed.fullUrl || '';
      if (effectiveBranch) {
        repoParam += `?branch=${effectiveBranch}`;
        if (path) {
          repoParam += `&path=${path}`;
        }
      } else if (path) {
        repoParam += `?path=${path}`;
      }
    }

    // Build query string manually to avoid encoding slashes
    let queryParams = `${paramName}=${repoParam}`;
    if (integrationType && integrationType !== 'default') {
      queryParams += `&t=${integrationType}`;
    }

    const deployUrl = `https://console.devant.dev/new?${queryParams}`;
    setGeneratedUrl(deployUrl);
  };

  const generateMarkdown = () => {
    if (!generatedUrl) return '';
    const imageName = buttonStyle === 'dark' ? 'images/DeployDevant-White.svg' : 'images/DeployDevant.svg';
    const imageUrl = `${window.location.origin}/${imageName}`;
    return `[![Deploy to Devant](${imageUrl})](${generatedUrl})`;
  };

  const generateHTML = () => {
    if (!generatedUrl) return '';
    const imageName = buttonStyle === 'dark' ? 'images/DeployDevant-White.svg' : 'images/DeployDevant.svg';
    const imageUrl = `${window.location.origin}/${imageName}`;
    return `<a href="${generatedUrl}" target="_blank">
  <img src="${imageUrl}" alt="Deploy to Devant" style="width: auto; height: 44px;" />
</a>`;
  };

  const generateMediumInstructions = () => {
    if (!generatedUrl) return '';
    return `${generatedUrl}`;
  };

  const handleCopy = (content: string, key: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopySuccess({ ...copySuccess, [key]: true });
      setTimeout(() => {
        setCopySuccess({ ...copySuccess, [key]: false });
      }, 2000);
    });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setIntegrationType(e.target.value);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header with Logo Placeholder */}
        <AppBar position="static" elevation={1} sx={{ borderRadius: 0, bgcolor: 'background.paper' }}>
          <Toolbar>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <img
                src="images/Devant_Logo_Blue.svg"
                alt="Devant Logo"
                style={{ height: '40px', width: 'auto' }}
              />
            </Box>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }} color="text.primary">
              Deploy to Devant Button Generator
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
          <Typography variant="body1" align="center" sx={{ mb: 4, color: 'text.primary' }}>
            Create an embedded button to let users deploy your repository to Devant with one
            click.
          </Typography>

          <Grid container spacing={3}>
            {/* Left Column - Form */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <Card elevation={2} sx={{ width: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src="images/Settings.svg" alt="" style={{ width: '24px', height: '24px' }} />
                    Configuration
                  </Typography>
                  <form onSubmit={handleGenerate}>
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        label={
                          <>
                            Repository URL <span style={{ color: 'red' }}>*</span>
                          </>
                        }
                        placeholder="https://github.com/username/repository"
                        value={githubUrl}
                        onChange={e => setGithubUrl(e.target.value)}
                        helperText="GitHub, GitLab, or Bitbucket URL"
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel id="integration-type-label">
                          Integration Type <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          labelId="integration-type-label"
                          value={integrationType}
                          onChange={handleSelectChange}
                          label="Integration Type"
                        >
                          {integrationTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label} - {type.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <TextField
                        label="Branch (Optional)"
                        placeholder="main"
                        value={branch}
                        onChange={e => setBranch(e.target.value)}
                        helperText="Specific branch to deploy from (e.g., main, develop). Defaults to main if not specified"
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <TextField
                        label="Directory Path (Optional)"
                        placeholder="services/api"
                        value={path}
                        onChange={e => setPath(e.target.value)}
                        helperText="Path to a specific directory within the repository"
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormLabel component="legend" sx={{ mb: 2, fontSize: '0.875rem' }}>
                        Button Style
                      </FormLabel>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: 2,
                        }}
                      >
                        <Box
                          onClick={() => setButtonStyle('light')}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            flex: { md: 1 },
                          }}
                        >
                          <Radio
                            checked={buttonStyle === 'light'}
                            value="light"
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1, flex: 1 }}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: '#f5f5f5',
                                borderRadius: 1,
                                border: buttonStyle === 'light' ? '2px solid' : '2px solid transparent',
                                borderColor: buttonStyle === 'light' ? 'primary.main' : 'transparent',
                                mb: 1,
                              }}
                            >
                              <img
                                src="/images/DeployDevant.svg"
                                alt="Light mode button"
                                style={{ height: '32px', width: 'auto', display: 'block' }}
                              />
                            </Box>
                            <Typography variant="caption">Light Background</Typography>
                          </Box>
                        </Box>
                        <Box
                          onClick={() => setButtonStyle('dark')}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            flex: { md: 1 },
                          }}
                        >
                          <Radio
                            checked={buttonStyle === 'dark'}
                            value="dark"
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1, flex: 1 }}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: '#1e1e1e',
                                borderRadius: 1,
                                border: buttonStyle === 'dark' ? '2px solid' : '2px solid transparent',
                                borderColor: buttonStyle === 'dark' ? 'primary.main' : 'transparent',
                                mb: 1,
                              }}
                            >
                              <img
                                src="/images/DeployDevant-White.svg"
                                alt="Dark mode button"
                                style={{ height: '32px', width: 'auto', display: 'block' }}
                              />
                            </Box>
                            <Typography variant="caption">Dark Background</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {error && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                      </Alert>
                    )}

                    <Button type="submit" variant="contained" size="large" fullWidth>
                      Generate Embedded Button
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Output */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              {generatedUrl ? (
                <Card elevation={2} sx={{ width: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img src="images/code.svg" alt="" style={{ width: '24px', height: '24px' }} />
                      Your Embedded Button Code
                    </Typography>

                    <Tabs
                      value={activeTab}
                      onChange={(_, newValue) => setActiveTab(newValue)}
                      sx={{ mb: 3 }}
                    >
                      <Tab label="Markdown" />
                      <Tab label="HTML" />
                      <Tab label="Medium" />
                    </Tabs>

                    {activeTab === 0 && (
                      <Box>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <Box
                            sx={{
                              p: 2,
                              pr: 6,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              overflow: 'auto',
                              maxHeight: '200px',
                              wordBreak: 'break-all',
                            }}
                          >
                            {generateMarkdown()}
                          </Box>
                          <Tooltip title={copySuccess.markdown ? 'Copied' : 'Copy'}>
                            <IconButton
                              onClick={() => handleCopy(generateMarkdown(), 'markdown')}
                              sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8,
                                color: copySuccess.markdown ? '#4caf50' : 'inherit'
                              }}
                              size="small"
                            >
                              {copySuccess.markdown ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}

                    {activeTab === 1 && (
                      <Box>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <Box
                            sx={{
                              p: 2,
                              pr: 6,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              overflow: 'auto',
                              maxHeight: '200px',
                              wordBreak: 'break-all',
                            }}
                          >
                            {generateHTML()}
                          </Box>
                          <Tooltip title={copySuccess.html ? 'Copied' : 'Copy'}>
                            <IconButton
                              onClick={() => handleCopy(generateHTML(), 'html')}
                              sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8,
                                color: copySuccess.html ? '#4caf50' : 'inherit'
                              }}
                              size="small"
                            >
                              {copySuccess.html ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}

                    {activeTab === 2 && (
                      <Box>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <Box
                            sx={{
                              p: 2,
                              pr: 6,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              overflow: 'auto',
                              maxHeight: '300px',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {generateMediumInstructions()}
                          </Box>
                          <Tooltip title={copySuccess.medium ? 'Copied' : 'Copy'}>
                            <IconButton
                              onClick={() => handleCopy(generateMediumInstructions(), 'medium')}
                              sx={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8,
                                color: copySuccess.medium ? '#4caf50' : 'inherit'
                              }}
                              size="small"
                            >
                              {copySuccess.medium ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}

                    <Box 
                      sx={{ 
                        mt: 3, 
                        p: 2, 
                        bgcolor: buttonStyle === 'dark' ? '#1e1e1e' : 'grey.50', 
                        borderRadius: 1 
                      }}
                    >
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom
                        sx={{ color: buttonStyle === 'dark' ? 'white' : 'inherit' }}
                      >
                        Preview:
                      </Typography>
                      <a
                        href={generatedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={buttonStyle === 'dark' ? '/images/DeployDevant-White.svg' : '/images/DeployDevant.svg'}
                          alt="Deploy to Devant"
                          style={{ width: 'auto', height: '44px' }}
                        />
                      </a>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card elevation={2} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                  <CardContent sx={{ p: 4, width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body1" align="center">
                        Fill out the form and generate your embed button to see the output here
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="xl">
            <Typography variant="body2" color="text.secondary" align="right">
              © 2025, WSO2 LLC.
            </Typography>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
